/**
 * @fileoverview Mailchimp Tracker - Storefront event tracking system for Mailchimp integration
 * 
 * This extension captures important storefront events (product views,
 * cart additions/removals/updates, checkout initiation) and stores them 
 * both in console and in a global array for future submission to Mailchimp.
 * 
 * @author NetSuite SuiteCommerce Team
 * @version 1.0.0
 */

define(
    'Mailchimp.Tracker',
    ['Utils', 'jQuery', 'Backbone', 'Mailchimp.SS2Model'],
    function (Utils, jQuery, Backbone, MailchimpSS2Model) {
        'use strict';

        // Global variable to store Mailchimp events
        window.__MAILCHIMP_TRACKER_EVENTS = window.__MAILCHIMP_TRACKER_EVENTS || [];

        var mailchimpSS2ModelInstance = null;
        var mailchimpConfig = {};

        /**
         * Generates a unique ID for each event
         * @returns {string} Simple UUID based on timestamp and random
         */
        function generateEventId() {
            var timestamp = Date.now().toString(36);
            var randomPart = Math.random().toString(36).substr(2, 5);
            return timestamp + '_' + randomPart;
        }

        /**
         * Generates a simple session ID (persists during browser session)
         * @returns {string} Session ID
         */
        function getSessionId() {
            if (!window.__MAILCHIMP_SESSION_ID) {
                window.__MAILCHIMP_SESSION_ID = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 8);
            }
            return window.__MAILCHIMP_SESSION_ID;
        }

        /**
         * Logs an event to console and stores it in the global array for Mailchimp
         * @param {Object} payload - Event data
         */
        function logEvent(payload) {
            try {
                // Add event metadata if not present
                if (!payload.event_id) {
                    payload.event_id = generateEventId();
                }

                if (!payload.session_id) {
                    payload.session_id = getSessionId();
                }

                // Store in global array
                window.__MAILCHIMP_TRACKER_EVENTS.push(payload);

                payload.connectorRecord = mailchimpConfig.connectorRecord || 1;

                if (mailchimpSS2ModelInstance) {
                    mailchimpSS2ModelInstance
                        .save(payload, { wait: true })
                        .done(function (result) {
                            console.log('[MAILCHIMP-TRACKER] SS2Model save result:', result);
                        })
                        .fail(function (xhr) {
                            console.error('[MAILCHIMP-TRACKER] Mailchimp.Tracker logEvent error', xhr);
                        });
                } else {
                    console.warn('[MAILCHIMP-TRACKER] SS2Model instance not initialized yet; event only stored locally.');
                }

            } catch (error) {
                console.error('[MAILCHIMP-TRACKER] Error processing event:', error);
            }
        }

        /**
         * Extracts product information from model for Mailchimp
         * @param {Object} product - Product model
         * @returns {Object} Basic product information
         */
        function extractProductInfo(pdp) {
            try {
                if (!pdp) return null;

                var itemInfo = pdp.getItemInfo();

                if (!itemInfo) {
                    return null;
                }

                var item = itemInfo.item;

                return {
                    id: item.internalid || item.id,
                    sku: item.itemid || item.sku,
                    name: item.displayname || item.name || item.storedisplayname || item.storedisplayname2,
                    price: item.onlinecustomerprice || itemInfo.price || item.onlinecustomerprice_detail || item.keyMapping_price_formatted,
                    url: item.canonicalurl || item.url || item.urlcomponent
                };
            } catch (error) {
                console.error('[MAILCHIMP-TRACKER] Error extracting product information:', error);
                return null;
            }
        }

        /**
         * Extracts cart line information for Mailchimp
         * @param {Object} line - Cart line
         * @returns {Object} Basic line information
         */
        function extractLineInfo(line) {
            try {
                if (!line) return null;

                // Handle different line object structures
                var lineData = line.line || line;
                var item = null;
                var lineId = null;
                var quantity = null;
                var price = null;

                // Extract line ID
                if (lineData.get && typeof lineData.get === 'function') {
                    lineId = lineData.get('internalid');
                    quantity = lineData.get('quantity');
                    price = lineData.get('rate') || lineData.get('amount');
                    item = lineData.get('item');
                } else {
                    lineId = lineData.internalid || lineData.result;
                    quantity = lineData.quantity;
                    price = lineData.rate || lineData.amount;
                    item = lineData.item;
                }

                var productId = null;
                var sku = null;
                var name = null;
                var itemType = null;
                var variantId = null;

                if (item) {
                    if (item.get && typeof item.get === 'function') {
                        var itemInternalId = item.get('internalid') || item.get('id');
                        var parentId = item.get('itemoptions_detail') ? item.get('itemoptions_detail').parentid : null;
                        productId = parentId || itemInternalId;
                        variantId = itemInternalId;
                        sku = item.get('itemid');
                        name = item.get('displayname') || item.get('name') || item.get('itemid');
                        itemType = item.get('itemtype') || item.get('type');
                    } else {
                        var itemInternalId = item.internalid || item.id;
                        var parentId = item.extras && item.extras.itemoptions_detail ? item.extras.itemoptions_detail.parentid : null;
                        productId = parentId || itemInternalId;
                        variantId = itemInternalId;
                        sku = item.itemid;
                        name = item.displayname || item.name || item.itemid;
                        itemType = item.itemtype || item.type;
                    }
                }

                var result = {
                    internalid: lineId,
                    product_id: productId ? productId.toString() : null,
                    product_variant_id: variantId ? variantId.toString() : (productId ? productId.toString() : null),
                    sku: sku,
                    name: name,
                    quantity: quantity,
                    price: price,
                    item_type: itemType
                };

                console.log('[MAILCHIMP-TRACKER] Final extracted line info:', result);
                return result;

            } catch (error) {
                console.error('[MAILCHIMP-TRACKER] Error extracting line information:', error);
                console.error('[MAILCHIMP-TRACKER] Line object that caused error:', line);
                return null;
            }
        }

        function ensureCartId() {
            try {
                var storageKey = 'mc_cart_id';

                if (window.sessionStorage) {
                    var existingId = window.sessionStorage.getItem(storageKey);
                    if (existingId) {
                        return existingId;
                    }
                    var newId = 'mc_cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 8);
                    window.sessionStorage.setItem(storageKey, newId);
                    return newId;
                }

                if (!window.__MAILCHIMP_CART_ID) {
                    window.__MAILCHIMP_CART_ID = 'mc_cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 8);
                }
                return window.__MAILCHIMP_CART_ID;

            } catch (e) {
                if (!window.__MAILCHIMP_CART_ID) {
                    window.__MAILCHIMP_CART_ID = 'mc_cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 8);
                }
                return window.__MAILCHIMP_CART_ID;
            }
        }

        /**
         * Gets the current cart ID (Mailchimp-specific, session-based)
         * @returns {string} Cart ID
         */
        function getCartId() {
            return ensureCartId();
        }

        function getUserProfileData(userProfileComponent) {
            if (!userProfileComponent || !userProfileComponent.getUserProfile) {
                return jQuery.Deferred().resolve(null).promise();
            }

            return userProfileComponent.getUserProfile().then(function (profile) {
                console.log('[MAILCHIMP-TRACKER] User Profile', profile);
                return profile;
            })['catch'](function (e) {
                console.error('[MAILCHIMP-TRACKER] Error getting user profile', e);
                return null;
            });
        }

        function buildCustomerFromProfile(profile) {
            if (!profile) {
                return null;
            }

            var email = profile.email || profile.emailaddress || profile.emailAddress;
            var opt_in_status = profile.emailsubscribe;

            if (!email) {
                return null;
            }

            // The email is the Mailchimp customer id: internalid is the NetSuite company,
            // shared by all its contacts, and Mailchimp ties a customer id to a single email
            email = String(email).trim().toLowerCase();

            return {
                id: email,
                email_address: email,
                opt_in_status: opt_in_status
            };
        }

        function getCurrencyCode(environment_component) {
            try {
                if (environment_component && environment_component.getConfig) {
                    var config = environment_component.getConfig('siteSettings');
                    if (config && config.currencycode) {
                        return config.currencycode;
                    }
                }

                if (window.SC && SC.ENVIRONMENT && SC.ENVIRONMENT.currentCurrency && SC.ENVIRONMENT.currentCurrency.code) {
                    return SC.ENVIRONMENT.currentCurrency.code;
                }
            } catch (e) {
                console.warn('[MAILCHIMP-TRACKER] Error getting currency code', e);
            }

            return null;
        }

        function buildCartPayload(options) {
            options = options || {};
            var cartId = getCartId();
            var singleLine = options.line || null;
            var lines = options.lines || null;
            var eventName = options.event;
            var environment_component = options.environment_component;
            var profile = options.userProfileData;

            var customer = buildCustomerFromProfile(profile);
            var currencyCode = getCurrencyCode(environment_component);

            var cart = {
                id: cartId
            };

            if (currencyCode) {
                cart.currency_code = currencyCode;
            }

            if (options.checkout_url) {
                cart.checkout_url = options.checkout_url;
            }

            cart.order_total = options.total || 0;

            // Build cart.lines with all current lines when provided
            if (lines && lines.length) {
                cart.lines = jQuery.map(lines, function (ln) {
                    if (!ln) {
                        return null;
                    }
                    return {
                        id: ln.internalid,
                        // Hardcoded product ids kept for testing
                        product_id: ln.product_id,
                        product_variant_id: ln.product_variant_id,
                        quantity: ln.quantity,
                        price: ln.price,
                        sku: ln.sku,
                        name: ln.name,
                        item_type: ln.item_type
                    };
                });
            } else if (singleLine) {
                cart.lines = [
                    {
                        id: singleLine.internalid,
                        // Hardcoded product ids kept for testing
                        product_id: singleLine.product_id,
                        product_variant_id: singleLine.product_variant_id,
                        quantity: singleLine.quantity,
                        price: singleLine.price,
                        sku: singleLine.sku,
                        name: singleLine.name,
                        item_type: singleLine.item_type
                    }
                ];
            }

            return {
                event: eventName,
                cart: cart,
                line: singleLine,
                customer: customer,
                cart_id: cartId
            };
        }

        return {
            /**
             * Main method that mounts the Mailchimp extension to the application
             * Executes when SuiteCommerce loads the extension
             * @param {Object} container - SCA component container
             */
            mountToApp: function mountToApp(container) {
                try {

                     
                    if (SC.isPageGenerator()) {
                        return;
                    }

                    // Get components following Klaviyo pattern
                    var cart = container.getComponent('Cart');
                    var pdp = container.getComponent('PDP');
                    var layout = container.getComponent('Layout');
                    var checkout = container.getComponent('Checkout');

                    var userProfileComponent = container.getComponent('UserProfile');
                    var environment_component = container.getComponent('Environment');

                    mailchimpConfig = environment_component.getConfig('tavanoMailchimp') || {};
                    console.log('[MAILCHIMP-TRACKER] Config:', mailchimpConfig);

                    if (!mailchimpConfig.enableMailchimp || mailchimpConfig.enableMailchimp !== true) {
                        return;
                    }

                    console.log('[MAILCHIMP-TRACKER] Initializing Mailchimp tracking system...');


                    mailchimpSS2ModelInstance = new MailchimpSS2Model();

                    // Sends the full SCA cart to Mailchimp. The user profile is read at send time
                    // and nothing is sent without a logged-in customer email (Mailchimp rejects it).
                    function sendCartEvent(eventName, lineData) {
                        getUserProfileData(userProfileComponent).then(function (profile) {
                            if (!buildCustomerFromProfile(profile)) {
                                console.warn('[MAILCHIMP-TRACKER] ' + eventName + ' not sent: missing customer email');
                                return;
                            }

                            cart.getSummary().then(function (summary) {
                                cart.getLines().then(function (linesData) {

                                    var lineInfo = lineData ? extractLineInfo(lineData) : null;
                                    var allLinesInfo = [];

                                    if (linesData && linesData.length) {
                                        for (var i = 0; i < linesData.length; i++) {
                                            var info = extractLineInfo(linesData[i]);
                                            if (info) {
                                                allLinesInfo.push(info);
                                            }
                                        }
                                    }

                                    if (lineData && !lineInfo) {
                                        console.warn('[MAILCHIMP-TRACKER] No line info extracted for ' + eventName);
                                        return;
                                    }

                                    if (!lineData && !allLinesInfo.length) {
                                        console.log('[MAILCHIMP-TRACKER] ' + eventName + ' not sent: cart is empty');
                                        return;
                                    }

                                    var payload = buildCartPayload({
                                        event: eventName,
                                        line: lineInfo,
                                        lines: allLinesInfo,
                                        total: summary.total || 0,
                                        // Link of the Mailchimp abandoned cart email: without it the link fails
                                        checkout_url: window.location.protocol + '//' + window.location.host + '/cart',
                                        environment_component: environment_component,
                                        userProfileData: profile
                                    });

                                    logEvent(payload);
                                });
                            });
                        });
                    }

                    // ---------------------
                    // Cart Events for Mailchimp
                    // ---------------------
                    if (cart) {
                        // Add to Cart (for Mailchimp Abandoned Cart)
                        cart.on("afterAddLine", function (lineData) {
                            try {
                                sendCartEvent('add_to_cart', lineData);
                            } catch (error) {
                                console.error('[MAILCHIMP-TRACKER] Error in add_to_cart tracking:', error);
                                console.error('[MAILCHIMP-TRACKER] Original line data:', lineData);
                            }
                        });

                        // Update Line
                        cart.on("afterUpdateLine", function (lineData) {
                            try {
                                sendCartEvent('update_line', lineData);
                            } catch (error) {
                                console.error('[MAILCHIMP-TRACKER] Error in update_line tracking:', error);
                                console.error('[MAILCHIMP-TRACKER] Original line data:', lineData);
                            }
                        });

                        // Remove Line
                        cart.on("afterRemoveLine", function (lineData) {
                            try {
                                console.log('[MAILCHIMP-TRACKER] afterRemoveLine triggered with data:', lineData);
                                sendCartEvent('remove_from_cart', lineData);
                            } catch (error) {
                                console.error('[MAILCHIMP-TRACKER] Error in remove_from_cart tracking:', error);
                                console.error('[MAILCHIMP-TRACKER] Original line data:', lineData);
                            }
                        });

                        console.log('[MAILCHIMP-TRACKER] Cart events tracking configured for Mailchimp');
                    } else {
                        console.warn('[MAILCHIMP-TRACKER] Cart component not available');
                    }

                    // ---------------------
                    // Product View & Checkout Tracking
                    // ---------------------
                    if (layout) {
                        layout.on('afterShowContent', function (view) {
                            try {

                                // Product View Tracking
                                console.log('[MAILCHIMP-TRACKER] afterShowContent triggered with view:', view);
                                console.log('[MAILCHIMP-TRACKER] PDP:', pdp);
                                if (pdp && view) {
                                    var productInfo = extractProductInfo(pdp);
                                    if (productInfo) {
                                        logEvent({
                                            event: 'product_view',
                                            product: productInfo
                                        });
                                    }
                                }

                            } catch (error) {
                                console.error('[MAILCHIMP-TRACKER] Error in afterShowContent tracking:', error);
                            }
                        });

                        console.log('[MAILCHIMP-TRACKER] Product view and checkout tracking configured for Mailchimp');
                    }


                    if (checkout) {
                        checkout.on("afterShowContent", function (data) {
                            isConfirmationStep().then(function (isConfirmation) {
                                if (isConfirmation) {
                                    completeCart();
                                    return;
                                }

                                // Re-sends the cart for shoppers who logged in after adding lines
                                // or whose cart comes from another session
                                if (cart) {
                                    sendCartEvent('sync_cart');
                                }
                                safeLogBeginCheckout();
                            });
                        });
                    }

                    function isConfirmationStep() {
                        var deferred = jQuery.Deferred();
                        var byUrl = /confirmation/i.test(window.location.href);

                        try {
                            if (checkout.getCurrentStep) {
                                checkout.getCurrentStep().then(function (step) {
                                    deferred.resolve(byUrl || !!(step && /confirmation/i.test(step.url || '')));
                                }, function () {
                                    deferred.resolve(byUrl);
                                });
                                return deferred.promise();
                            }
                        } catch (e) {
                            console.warn('[MAILCHIMP-TRACKER] Error getting checkout step', e);
                        }

                        return deferred.resolve(byUrl).promise();
                    }

                    // Order placed: removes the cart from Mailchimp so no abandoned cart email is sent
                    // and clears the stored ids so the next purchase starts a new cart
                    function completeCart() {
                        var storageKey = 'mc_cart_id';
                        var cartId = null;

                        try {
                            cartId = window.sessionStorage.getItem(storageKey);
                        } catch (e) {
                            cartId = null;
                        }
                        cartId = cartId || window.__MAILCHIMP_CART_ID;

                        if (!cartId) {
                            return;
                        }

                        logEvent({
                            event: 'cart_completed',
                            cart: { id: cartId },
                            cart_id: cartId
                        });

                        try {
                            window.sessionStorage.removeItem(storageKey);
                            window.sessionStorage.removeItem('mc_begin_checkout_sent__' + cartId);
                        } catch (e) {
                            console.warn('[MAILCHIMP-TRACKER] Error clearing cart storage', e);
                        }
                        window.__MAILCHIMP_CART_ID = null;
                        if (window.__MAILCHIMP_BEGIN_CHECKOUT) {
                            delete window.__MAILCHIMP_BEGIN_CHECKOUT[cartId];
                        }

                        console.log('[MAILCHIMP-TRACKER] cart_completed logged:', cartId);
                    }

                    // Ensure begin_checkout is only sent once per cart/session
                    function markBeginCheckoutSent(cartId) {
                        if (!cartId) return false;
                        try {
                            var key = 'mc_begin_checkout_sent__' + cartId;
                            if (window.sessionStorage.getItem(key)) {
                                return true;
                            }
                            window.sessionStorage.setItem(key, '1');
                            return false;
                        } catch (e) {
                            window.__MAILCHIMP_BEGIN_CHECKOUT = window.__MAILCHIMP_BEGIN_CHECKOUT || {};
                            if (window.__MAILCHIMP_BEGIN_CHECKOUT[cartId]) return true;
                            window.__MAILCHIMP_BEGIN_CHECKOUT[cartId] = true;
                            return false;
                        }
                    }

                    function safeLogBeginCheckout(cartId) {
                        getUserProfileData(userProfileComponent).then(function (profile) {
                            try {
                                if (!cartId) {
                                    cartId = getCartId();
                                }

                                // Checked before marking as sent, so a later step can still send it
                                var customer = buildCustomerFromProfile(profile);
                                if (!customer || !customer.email_address) {
                                    console.warn('[MAILCHIMP-TRACKER] begin_checkout not sent: missing customer email');
                                    return;
                                }

                                if (markBeginCheckoutSent(cartId)) {
                                    console.log('[MAILCHIMP-TRACKER] begin_checkout already sent for', cartId);
                                    return;
                                }

                                var properties = {
                                    cart_id: cartId,
                                    checkout_url: window.location.href
                                };

                                logEvent({
                                    event: 'begin_checkout',
                                    customer: customer,
                                    cart_id: cartId,
                                    checkout_url: window.location.href,
                                    properties: properties,
                                    userProfileData: profile
                                });
                                console.log('[MAILCHIMP-TRACKER] begin_checkout logged (source):', cartId);
                            } catch (err) {
                                console.error('[MAILCHIMP-TRACKER] safeLogBeginCheckout error:', err);
                            }
                        });
                    }

                    console.log('[MAILCHIMP-TRACKER] Mailchimp tracking system initialized successfully');


                } catch (error) {
                    console.error('[MAILCHIMP-TRACKER] Critical error initializing Mailchimp tracker:', error);
                }
            }



        };



    }
);

